import React, { memo } from 'react';
import { Cell, DropdownIcon, Input, Tooltip } from '@cognitiv/cyprus-ui';
import { faCheckCircle, faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ParametersOption } from 'ducks/types/common-types';
import {
  ActionCellProps,
  DefaultCellProps,
  DescriptionCellProps,
  MethodCellProps,
  ParameterNameCellProps,
  RequiredCellProps,
} from 'ducks/types/table-types';
import { useSwitchTheme } from 'hooks/useSwitchTheme';

import cn from 'modals/parameters/components/Components.module.scss';
const { PUBLIC_URL } = process.env;

const ParameterNameCellBase = ({ row, handleParameterName, settings }: ParameterNameCellProps) => {
  const { theme, is_dark_theme } = useSwitchTheme();

  return (
    <Cell
      background={row?.error && row.modified && (is_dark_theme ? '#414043' : '#fff3eb')}
      height={settings?.row_height}
    >
      <Input
        placeholder="&#xFF0B; Add a Parameter"
        controlled={false}
        default_value={row?.parameter_name}
        onChange={(value: string) => row && handleParameterName(value, row)}
        transparent
        theme={{ ...theme.inputs, input_font_color_placeholder: '#276EF1' }}
      />
    </Cell>
  );
};
export const ParameterNameCell = memo(ParameterNameCellBase);

const RequiredCellBase = ({ row, handleRequired, settings }: RequiredCellProps) => {
  const hex_code = row?.is_required ? '#5db85b' : '#adbbd5';
  const tooltip = row?.is_required ? 'Required' : 'Not Required';
  const { theme, is_dark_theme } = useSwitchTheme();

  if (!row?.modified) {
    return <Cell height={settings?.row_height} />;
  }
  return (
    <Cell
      background={row?.error && (is_dark_theme ? '#414043' : '#fff3eb')}
      align="center"
      height={settings?.row_height}
      onClick={() => row && handleRequired(!row.is_required, row)}
    >
      <div className={cn.action}>
        <Tooltip
          theme={theme.tooltip}
          tooltip={tooltip}
          position={{ top: 3, left: 'calc(100% + 10px)' }}
          width="max-content"
          caret="left"
        />
        <FontAwesomeIcon icon={faCheckCircle} color={hex_code} />
      </div>
    </Cell>
  );
};
export const RequiredCell = memo(RequiredCellBase);

const MethodCellBase = ({ row, editParameter, settings }: MethodCellProps) => {
  const { theme, is_dark_theme } = useSwitchTheme();
  const icon =
    row?.parameter_method_id === 1
      ? 'command_line.svg'
      : !row?.is_encrypted
      ? 'environment_variable.svg'
      : 'environment_locked.svg';
  const tooltip =
    row?.parameter_method_id === 1
      ? 'Command Line'
      : row?.is_encrypted
      ? 'Environment Variable Encrypted'
      : 'Environment Variable Decrypted';
  if (!row?.modified) {
    return <Cell height={settings?.row_height} />;
  }
  return (
    <Cell
      background={row?.error && (is_dark_theme ? '#414043' : '#fff3eb')}
      onClick={() => editParameter(row)}
      align="center"
      height={settings?.row_height}
    >
      <div className={cn.customAction} style={{ height: settings?.row_height }}>
        <Tooltip
          theme={theme.tooltip}
          tooltip={tooltip}
          position={{ top: 3, right: 'calc(100% + 10px)' }}
          width="max-content"
          caret="right"
        />
        <img
          src={`${PUBLIC_URL}/${icon}`}
          alt={tooltip}
          height={!row?.is_encrypted && row?.parameter_method_id !== 1 ? 18 : 22}
        />
      </div>
    </Cell>
  );
};
export const MethodCell = memo(MethodCellBase);

const DefaultCellBase = ({ row, handleParameterDefault, settings }: DefaultCellProps) => {
  const { is_dark_theme } = useSwitchTheme();
  if (!row?.modified) {
    return <Cell height={settings?.row_height} />;
  }

  let background = null;

  if (row?.reference_type_id) {
    background = '#f2f3f4';
  }
  if (row?.error) {
    background = is_dark_theme ? '#414043' : '#fff3eb';
  }

  return (
    <Cell background={background} height={settings?.row_height}>
      <Input
        controlled={false}
        height={settings?.row_height}
        default_value={
          row?.reference_type_id ? `${row.reference_type_name}: ${row.reference_parameter_name}` : row.parameter_value
        }
        disabled={!!row?.reference_type_id}
        onChange={(input: string) => handleParameterDefault(input, row)}
        transparent
      />
    </Cell>
  );
};
export const DefaultCell = memo(DefaultCellBase);

const DescriptionCellBase = ({ row, handleParameterDescription, settings }: DescriptionCellProps) => {
  const { is_dark_theme } = useSwitchTheme();
  if (!row?.modified) {
    return <Cell height={settings?.row_height} />;
  }

  let background = null;

  if (row?.reference_type_id) {
    background = '#f2f3f4';
  }
  if (row?.error) {
    background = is_dark_theme ? '#414043' : '#fff3eb';
  }

  return (
    <Cell background={background} height={settings?.row_height}>
      <Input
        controlled={false}
        default_value={row.description}
        height={settings?.row_height}
        disabled={!!row?.reference_type_id}
        onChange={(input: string) => handleParameterDescription(input, row)}
        transparent
      />
    </Cell>
  );
};
export const DescriptionCell = memo(DescriptionCellBase);

const ActionCellBase = ({ row, handleActions, settings }: ActionCellProps) => {
  const { is_dark_theme } = useSwitchTheme();
  const options: ParametersOption[] = [
    {
      name: 'Edit Parameter',
      trigger: 'edit',
      uuid: 1,
    },
    {
      name: 'Remove Parameter',
      trigger: 'delete',
      uuid: 2,
    },
  ];

  if (!row?.modified) {
    return <Cell />;
  }

  return (
    <Cell background={row?.error && (is_dark_theme ? '#414043' : '#fff3eb')}>
      <div className={cn.dropDownWrapper}>
        <DropdownIcon
          icon={faEllipsisV}
          options={options}
          margin="0 auto"
          height={`${settings?.row_height}px`}
          transparent
          onSelect={(item: ParametersOption) =>
            handleActions({
              ...item,
              row,
            })
          }
        />
      </div>
    </Cell>
  );
};
export const ActionCell = memo(ActionCellBase);
